/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AutocompleteRenderInputParams, FormControl, OutlinedInput, Typography } from '@mui/material';
import { ResponsiveLine } from '@nivo/line';

import { CIColors, Endpoints, Infer } from 'shared';

import { ChartTooltip } from '../../../../components/ChartTooltip/ChartTooltip';
import { ArrowRoundedIcon } from '../../../../components/svg-icons/ArrowRoundedIcon';
import { BaseLineChartData } from '../../../../constants/chartConfig';
import { HUAutocomplete } from '../../../../elements/HUAutocomplete/HUAutocomplete';
import { CapacityUnits, UnitsToBytesMultiplier } from '../../../../elements/HUBytesInput/components/UnitPicker.types';
import { destructRenderInputParams } from '../../../../elements/HUTextInput/utils';

type BackupsChartProps = {
  backupsHistory: Infer<typeof Endpoints.GetClusterBackupsHistory>['Response']['200'];
};

export const BackupsChart = memo<BackupsChartProps>(props => {
  const { backupsHistory } = props;

  const { t, i18n } = useTranslation();

  const today = new Date();

  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());

  const data = useMemo(() => {
    const tmpChartData: BaseLineChartData[] = [];

    const history = backupsHistory.find(bh => bh.year === selectedYear && bh.month === selectedMonth);

    if (history && (history.backups.continuous.length > 3 || history.backups.logical.length > 3)) {
      history.backups.continuous.length &&
        tmpChartData.push({
          id: 'continuous',
          data: history.backups.continuous.map(backup => ({
            x: new Date(backup.lastModified),
            y: (backup.sizeInBytes / UnitsToBytesMultiplier[CapacityUnits.MB]).toFixed(2),
          })),
          color: CIColors.Tertiary,
        });

      history.backups.logical.length &&
        tmpChartData.push({
          id: 'logical',
          data: history.backups.logical.map(backup => ({
            x: new Date(backup.lastModified),
            y: (backup.sizeInBytes / UnitsToBytesMultiplier[CapacityUnits.MB]).toFixed(2),
          })),
          color: CIColors.Secondary,
        });
    }

    return tmpChartData;
  }, [backupsHistory, selectedYear, selectedMonth]);

  const years = useMemo(() => {
    const yearsSet = new Set<number>();
    backupsHistory.forEach(bh => {
      yearsSet.add(bh.year);
    });
    return Array.from(yearsSet);
  }, [backupsHistory]);

  const tickValues = useMemo(() => {
    const backups = backupsHistory.find(bh => bh.year === selectedYear && bh.month === selectedMonth)?.backups;
    const sumRecords = (backups?.continuous.length || 0) + (backups?.logical.length || 0);
    const interval = Math.trunc(sumRecords / 5);
    return sumRecords < 5 ? 'every day' : `every ${interval} days`;
  }, [backupsHistory, selectedYear, selectedMonth]);

  const renderAutocompleteInput = (renderParams: AutocompleteRenderInputParams) => (
    <OutlinedInput {...destructRenderInputParams(renderParams)} size="small" />
  );

  const maxY = useMemo(() => {
    if (data.length) {
      const sizes = data[0].data.map(d => Number(d.y));
      const max = Math.max(...sizes);
      return max > 10 ? max + 3 : 10;
    }
  }, [data]);

  return (
    <>
      <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <Typography variant="h4" sx={{ mr: '15px' }}>
          {t('cluster:details.backups.size_history_title')}
        </Typography>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginRight: '148px' }}>
          <FormControl sx={{ width: '150px' }}>
            <HUAutocomplete
              renderInput={renderAutocompleteInput}
              getOptionLabel={opt => new Date(2000, --opt, 2).toLocaleString(i18n.language, { month: 'long' })}
              options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]}
              popupIcon={<ArrowRoundedIcon />}
              value={selectedMonth}
              disableClearable
              onChange={(_e, value) => value && setSelectedMonth(value)}
            />
          </FormControl>
          {years.length > 1 && (
            <FormControl sx={{ width: '100px' }}>
              <HUAutocomplete
                renderInput={renderAutocompleteInput}
                getOptionLabel={opt => `${opt}`}
                options={years}
                popupIcon={<ArrowRoundedIcon />}
                value={selectedYear}
                disableClearable
                onChange={(_e, value) => value && setSelectedYear(value)}
              />
            </FormControl>
          )}
        </div>
      </div>
      <div style={{ overflow: 'visible', height: '475px', width: '100%', position: 'relative' }}>
        <ResponsiveLine
          data={data}
          margin={{ top: 10, right: 150, bottom: 40, left: 40 }}
          yScale={{ type: 'linear', min: 0, max: maxY, reverse: false }}
          xScale={{ type: 'time', precision: 'day' }}
          pointSize={10}
          useMesh
          colors={series => series.color}
          enableCrosshair={false}
          tooltip={a => (
            <ChartTooltip label={new Date(String(a.point.data.x)).toLocaleDateString()} value={`${a.point.data.y}MB`} />
          )}
          axisBottom={{
            format: value => new Date(value).toLocaleDateString(),
            tickValues,
          }}
          legends={[
            {
              anchor: 'bottom-right',
              direction: 'column',
              translateX: 100,
              itemWidth: 80,
              itemHeight: 22,
              symbolShape: 'circle',
              data: data.length
                ? [
                    { id: 'continuous', label: t('cluster:details.backups.continuous'), color: CIColors.Tertiary },
                    { id: 'logical', label: t('cluster:details.backups.logical'), color: CIColors.Secondary },
                  ]
                : undefined,
            },
          ]}
        />
        {!data.length && (
          <Typography
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-57%, -50%)',
              textAlign: 'center',
              color: CIColors.GraphicElementsHover,
            }}
          >
            {t('cluster:details.backups.no_size_history')}
          </Typography>
        )}
      </div>
    </>
  );
});
